import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, Search, Edit, Archive, ExternalLink, Copy, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

const TOOL_LABELS = {
  bando_nazionale:"Bando nazionale", bando_regionale:"Bando regionale", bando_camerale:"Bando camerale",
  misura_invitalia:"Misura Invitalia", incentivo_fiscale:"Incentivo fiscale", credito_imposta:"Credito d'imposta",
  finanza_ordinaria:"Finanza ordinaria", microcredito:"Microcredito", finanziamento_mcc:"Finanziamento MCC",
  mutuo:"Mutuo/Prestito", leasing:"Leasing", certificazione:"Certificazione", altro:"Altro",
};
const STATUS_LABELS = { bozza:"Bozza", annunciato:"Annunciato", in_apertura:"In apertura", aperto:"Aperto",
  sospeso:"Sospeso", chiuso:"Chiuso", esaurito:"Esaurito", prorogato:"Prorogato", archiviato:"Archiviato" };
const SCOPE_LABELS = { nazionale:"Nazionale", regionale:"Regionale", provinciale:"Provinciale", comunale:"Comunale", ue:"UE" };

export function BandiListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = async () => {
    const params = { limit: 500 };
    if (q) params.q = q;
    if (typeF) params.tool_type = typeF;
    if (statusF) params.status = statusF;
    if (includeArchived) params.include_archived = true;
    const r = await api.get("/bandi", { params });
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, [q, typeF, statusF, includeArchived]);

  const now = new Date();
  const in7 = new Date(now.getTime() + 7*24*3600*1000);

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="bandi-title">Bandi e strumenti</h1>
        <div className="ma-quick-actions">
          <Link to="/bandi/new" className="ma-btn-primary-sm" data-testid="btn-new-bando"><Plus size={16}/> Nuovo bando/strumento</Link>
        </div>
      </div>
      <div className="ma-filters">
        <div className="ma-search"><Search size={14}/><input placeholder="Cerca per titolo, ente, finanziatore…" value={q} onChange={e=>setQ(e.target.value)} data-testid="bando-search"/></div>
        <select className="ma-input-sm" value={typeF} onChange={e=>setTypeF(e.target.value)} data-testid="bando-type-filter">
          <option value="">Tutte le tipologie</option>
          {Object.entries(TOOL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ma-input-sm" value={statusF} onChange={e=>setStatusF(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="ma-checkbox" style={{marginLeft:8}}>
          <input type="checkbox" checked={includeArchived} onChange={e=>setIncludeArchived(e.target.checked)}/> Includi archiviati
        </label>
        <div className="ma-total">Totale: {items.length}</div>
      </div>

      <div className="ma-table-wrap"><table className="ma-table">
        <thead><tr><th>Codice</th><th>Titolo</th><th>Tipologia</th><th>Ente</th><th>Ambito</th><th>Chiusura</th><th>Stato</th></tr></thead>
        <tbody>
          {items.map(b => {
            const closing = b.closing_date_time ? new Date(b.closing_date_time) : null;
            const soon = closing && closing >= now && closing <= in7;
            return (
              <tr key={b.id} data-testid={`bando-row-${b.id}`}>
                <td><span className="ma-code-badge">{b.code}</span></td>
                <td><Link to={`/bandi/${b.id}`} className="ma-link">{b.title}</Link></td>
                <td>{TOOL_LABELS[b.tool_type] || b.tool_type}</td>
                <td>{b.issuing_body || "—"}</td>
                <td>{SCOPE_LABELS[b.territorial_scope] || (b.territorial_scope || "—")}</td>
                <td>{closing ? <span style={{color: soon?"#FF7A7A":"inherit", fontWeight: soon?600:400}}>
                  {closing.toLocaleString("it-IT", {day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                  {soon && " ⚠"}</span> : "—"}</td>
                <td><span className={`ma-pill ${b.archived_at ? "pill-gray" : b.status==="aperto" ? "pill-ok" : b.status==="chiuso" ? "pill-danger" : "pill-warn"}`}>
                  {STATUS_LABELS[b.status] || b.status}</span></td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={7} className="ma-empty">Nessun bando/strumento presente.</td></tr>}
        </tbody>
      </table></div>
    </div>
  );
}

export function BandoFormPage({ edit=false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState({
    title:"", tool_type:"bando_nazionale", status:"aperto", territorial_scope:"nazionale",
    issuing_body:"", funder:"", procedure:"", publication_date:"", opening_date:"", closing_date_time:"",
    recipients:"", legal_forms:"", company_sizes:"", ateco_included:"", ateco_excluded:"",
    requirements:"", eligible_expenses:"", excluded_expenses:"",
    investment_min:"", investment_max:"", grant_min:"", grant_max:"",
    aid_type:"", max_intensity_percent:"", aid_regime:"", available_resources:"",
    official_url:"", legal_url:"", last_verified_at:"", description:"", notes:"", tags:[],
  });
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (edit && id) {
      api.get(`/bandi/${id}`).then(r => {
        const d = r.data;
        setF({
          ...d,
          publication_date: d.publication_date || "",
          opening_date: d.opening_date || "",
          closing_date_time: d.closing_date_time ? d.closing_date_time.slice(0, 16) : "",
          last_verified_at: d.last_verified_at || "",
          investment_min: d.investment_min ?? "",
          investment_max: d.investment_max ?? "",
          grant_min: d.grant_min ?? "",
          grant_max: d.grant_max ?? "",
          max_intensity_percent: d.max_intensity_percent ?? "",
          available_resources: d.available_resources ?? "",
          tags: d.tags || [],
        });
      }).catch(() => {});
    }
  }, [edit, id]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !f.tags.includes(t)) setF({...f, tags:[...f.tags, t]});
    setTagInput("");
  };
  const removeTag = (t) => setF({...f, tags: f.tags.filter(x => x !== t)});

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f };
      ["publication_date","opening_date","closing_date_time","last_verified_at"].forEach(k => { if (!p[k]) p[k] = null; });
      if (p.closing_date_time) p.closing_date_time = new Date(p.closing_date_time).toISOString();
      ["investment_min","investment_max","grant_min","grant_max","max_intensity_percent","available_resources"].forEach(k => {
        p[k] = p[k] === "" || p[k] == null ? null : parseFloat(p[k]);
      });
      let res;
      if (edit && id) res = await api.patch(`/bandi/${id}`, p);
      else res = await api.post("/bandi", p);
      toast.success(edit ? "Bando aggiornato" : `Bando creato (${res.data.code})`);
      nav(`/bandi/${res.data.id}`);
    } catch { setBusy(false); }
  };

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/bandi">Bandi e strumenti</Link> · {edit ? "Modifica" : "Nuovo"}</div>
          <h1 className="ma-h1">{edit ? "Modifica bando" : "Nuovo bando/strumento"}</h1>
        </div>
      </div>
      <form onSubmit={submit} className="ma-form-panel">
        <div className="ma-form-grid">
          <div className="ma-col-full"><label className="ma-label">Titolo *</label>
            <input className="ma-input" required value={f.title} onChange={e=>setF({...f, title:e.target.value})} data-testid="bf-title"/></div>
          <div><label className="ma-label">Tipologia *</label>
            <select className="ma-input" value={f.tool_type} onChange={e=>setF({...f, tool_type:e.target.value})} data-testid="bf-type">
              {Object.entries(TOOL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="ma-label">Stato</label>
            <select className="ma-input" value={f.status} onChange={e=>setF({...f, status:e.target.value})}>
              {Object.entries(STATUS_LABELS).filter(([k])=>k!=="archiviato").map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="ma-label">Ambito territoriale</label>
            <select className="ma-input" value={f.territorial_scope} onChange={e=>setF({...f, territorial_scope:e.target.value})}>
              {Object.entries(SCOPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="ma-label">Procedura</label>
            <select className="ma-input" value={f.procedure||""} onChange={e=>setF({...f, procedure:e.target.value})}>
              <option value="">—</option>
              <option value="sportello">A sportello</option>
              <option value="graduatoria">Graduatoria</option>
            </select></div>
          <div><label className="ma-label">Ente gestore</label>
            <input className="ma-input" value={f.issuing_body||""} onChange={e=>setF({...f, issuing_body:e.target.value})}/></div>
          <div><label className="ma-label">Soggetto finanziatore</label>
            <input className="ma-input" value={f.funder||""} onChange={e=>setF({...f, funder:e.target.value})}/></div>
          <div><label className="ma-label">Data pubblicazione</label>
            <input type="date" className="ma-input" value={f.publication_date} onChange={e=>setF({...f, publication_date:e.target.value})}/></div>
          <div><label className="ma-label">Data apertura</label>
            <input type="date" className="ma-input" value={f.opening_date} onChange={e=>setF({...f, opening_date:e.target.value})}/></div>
          <div><label className="ma-label">Data e ora chiusura</label>
            <input type="datetime-local" className="ma-input" value={f.closing_date_time} onChange={e=>setF({...f, closing_date_time:e.target.value})}/></div>
          <div><label className="ma-label">Data ultima verifica</label>
            <input type="date" className="ma-input" value={f.last_verified_at} onChange={e=>setF({...f, last_verified_at:e.target.value})}/></div>
          <div><label className="ma-label">Tipologia aiuto</label>
            <select className="ma-input" value={f.aid_type||""} onChange={e=>setF({...f, aid_type:e.target.value})}>
              <option value="">—</option>
              <option value="fondo_perduto">Fondo perduto</option>
              <option value="finanziamento_agevolato">Finanziamento agevolato</option>
              <option value="garanzia">Garanzia</option>
              <option value="credito_imposta">Credito d'imposta</option>
              <option value="combinazione">Combinazione</option>
            </select></div>
          <div><label className="ma-label">Regime di aiuto</label>
            <select className="ma-input" value={f.aid_regime||""} onChange={e=>setF({...f, aid_regime:e.target.value})}>
              <option value="">—</option><option value="de_minimis">De minimis</option>
              <option value="regolamento_esenzione">Regolamento esenzione</option>
              <option value="notificato">Notificato</option><option value="altro">Altro</option>
            </select></div>
          <div><label className="ma-label">% intensità massima</label>
            <input type="number" step="0.01" className="ma-input" value={f.max_intensity_percent} onChange={e=>setF({...f, max_intensity_percent:e.target.value})}/></div>
          <div><label className="ma-label">Investimento minimo (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.investment_min} onChange={e=>setF({...f, investment_min:e.target.value})}/></div>
          <div><label className="ma-label">Investimento massimo (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.investment_max} onChange={e=>setF({...f, investment_max:e.target.value})}/></div>
          <div><label className="ma-label">Agevolazione minima (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.grant_min} onChange={e=>setF({...f, grant_min:e.target.value})}/></div>
          <div><label className="ma-label">Agevolazione massima (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.grant_max} onChange={e=>setF({...f, grant_max:e.target.value})}/></div>
          <div><label className="ma-label">Risorse disponibili (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.available_resources} onChange={e=>setF({...f, available_resources:e.target.value})}/></div>
          <div className="ma-col-full"><label className="ma-label">Descrizione</label>
            <textarea className="ma-input" rows={3} value={f.description||""} onChange={e=>setF({...f, description:e.target.value})}/></div>
          <div className="ma-col-full"><label className="ma-label">Destinatari</label>
            <textarea className="ma-input" rows={2} value={f.recipients||""} onChange={e=>setF({...f, recipients:e.target.value})}/></div>
          <div className="ma-col-2"><label className="ma-label">Forme giuridiche ammesse</label>
            <input className="ma-input" value={f.legal_forms||""} onChange={e=>setF({...f, legal_forms:e.target.value})}/></div>
          <div className="ma-col-2"><label className="ma-label">Dimensioni impresa</label>
            <input className="ma-input" value={f.company_sizes||""} onChange={e=>setF({...f, company_sizes:e.target.value})} placeholder="Micro, Piccola, Media, Grande"/></div>
          <div className="ma-col-2"><label className="ma-label">ATECO ammessi</label>
            <input className="ma-input" value={f.ateco_included||""} onChange={e=>setF({...f, ateco_included:e.target.value})}/></div>
          <div className="ma-col-2"><label className="ma-label">ATECO esclusi</label>
            <input className="ma-input" value={f.ateco_excluded||""} onChange={e=>setF({...f, ateco_excluded:e.target.value})}/></div>
          <div className="ma-col-full"><label className="ma-label">Requisiti principali</label>
            <textarea className="ma-input" rows={2} value={f.requirements||""} onChange={e=>setF({...f, requirements:e.target.value})}/></div>
          <div className="ma-col-2"><label className="ma-label">Spese ammissibili</label>
            <textarea className="ma-input" rows={2} value={f.eligible_expenses||""} onChange={e=>setF({...f, eligible_expenses:e.target.value})}/></div>
          <div className="ma-col-2"><label className="ma-label">Spese escluse</label>
            <textarea className="ma-input" rows={2} value={f.excluded_expenses||""} onChange={e=>setF({...f, excluded_expenses:e.target.value})}/></div>
          <div><label className="ma-label">Link ufficiale</label>
            <input className="ma-input" value={f.official_url||""} onChange={e=>setF({...f, official_url:e.target.value})}/></div>
          <div><label className="ma-label">Link normativa</label>
            <input className="ma-input" value={f.legal_url||""} onChange={e=>setF({...f, legal_url:e.target.value})}/></div>
          <div className="ma-col-full">
            <label className="ma-label">Tag / parole chiave</label>
            <div style={{display:"flex", gap:6, flexWrap:"wrap", alignItems:"center"}}>
              {f.tags.map(t => <span key={t} className="ma-pill">
                {t} <button type="button" onClick={()=>removeTag(t)} style={{background:"none",border:"none",cursor:"pointer",marginLeft:4}}>×</button>
              </span>)}
              <input className="ma-input-sm" placeholder="Aggiungi tag e Invio…" value={tagInput}
                     onChange={e=>setTagInput(e.target.value)}
                     onKeyDown={e=>{ if (e.key === "Enter") { e.preventDefault(); addTag(); }}}
                     style={{flex:1, minWidth:160}}/>
            </div>
          </div>
          <div className="ma-col-full"><label className="ma-label">Note operative</label>
            <textarea className="ma-input" rows={3} value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/></div>
        </div>
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={()=>nav(-1)}>Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="bf-submit">
            {busy ? "Salvataggio…" : (edit ? "Salva modifiche" : "Crea bando")}
          </button>
        </div>
      </form>
    </div>
  );
}

const EVAL_LABELS = {
  da_valutare:"Da valutare", potenzialmente_compatibile:"Potenzialmente compatibile",
  non_compatibile:"Non compatibile", proposta_al_cliente:"Proposta al cliente",
  scelta_dal_cliente:"Scelta dal cliente", scartata:"Scartata",
  trasformata_in_pratica:"Trasformata in pratica",
};

export function BandoDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [tab, setTab] = useState("info");
  const [addEvalOpen, setAddEvalOpen] = useState(false);

  const load = () => api.get(`/bandi/${id}`).then(r => setB(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const doArchive = async () => {
    if (!window.confirm("Archiviare questo bando?")) return;
    await api.post(`/bandi/${id}/archive`); toast.success("Archiviato"); load();
  };
  const doDuplicate = async () => {
    const r = await api.post(`/bandi/${id}/duplicate`);
    toast.success(`Duplicato come ${r.data.code}`);
    nav(`/bandi/${r.data.id}/edit`);
  };
  const createPractice = async () => {
    // Create a practice from the bando context - simplified: navigate to practices/new with prefilled query
    const subj = prompt("Inserisci ID cliente per creare pratica (deve esistere in Clienti). Vuoto per annullare.");
    if (!subj) return;
    try {
      const res = await api.post("/practices", {
        title: `[${b.code}] ${b.title}`, subject_id: subj, service_type: "finanza_agevolata",
        tool_note: b.title, description: `Pratica generata dal bando ${b.code}`, deadline: b.closing_date_time?.slice(0,10) || null,
      });
      await api.patch(`/practices/${res.data.id}`);
      // link bando to practice
      await api.patch(`/practices/${res.data.id}`, {}); // no-op; the direct bando_id link can be done via server if we add endpoint. Skipping for MVP.
      toast.success(`Pratica creata: ${res.data.code}`);
      nav(`/practices/${res.data.id}`);
    } catch {}
  };

  if (!b) return <div className="ma-loading">Caricamento…</div>;
  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/bandi">Bandi e strumenti</Link></div>
          <h1 className="ma-h1"><span className="ma-code-badge">{b.code}</span> {b.title}</h1>
          <div className="ma-sub">
            <span className="ma-pill">{TOOL_LABELS[b.tool_type]}</span>
            <span className={`ma-pill ${b.archived_at?"pill-gray":b.status==="aperto"?"pill-ok":b.status==="chiuso"?"pill-danger":"pill-warn"}`}>{STATUS_LABELS[b.status]}</span>
            {b.closing_date_time && <span className="ma-pill pill-warn">Chiusura: {new Date(b.closing_date_time).toLocaleString("it-IT")}</span>}
          </div>
        </div>
        <div className="ma-quick-actions">
          {!b.archived_at && <Link to={`/bandi/${id}/edit`} className="ma-btn-secondary"><Edit size={14}/> Modifica</Link>}
          <button className="ma-btn-secondary" onClick={doDuplicate}><Copy size={14}/> Duplica</button>
          {!b.archived_at && <button className="ma-btn-secondary" onClick={doArchive}><Archive size={14}/> Archivia</button>}
        </div>
      </div>

      <div className="ma-tabs">
        <button className={`ma-tab ${tab==="info"?"active":""}`} onClick={()=>setTab("info")}>Dettagli</button>
        <button className={`ma-tab ${tab==="evals"?"active":""}`} onClick={()=>setTab("evals")}>Valutazioni cliente ({b.evaluations?.length || 0})</button>
        <button className={`ma-tab ${tab==="practices"?"active":""}`} onClick={()=>setTab("practices")}>Pratiche ({b.linked_practices?.length || 0})</button>
      </div>

      {tab === "info" && (
        <div className="ma-info-grid">
          <div className="ma-info-block">
            <h3 className="ma-h3">Panoramica</h3>
            <div className="ma-row-kv"><div className="ma-row-k">Ente gestore</div><div className="ma-row-v">{b.issuing_body||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Finanziatore</div><div className="ma-row-v">{b.funder||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Ambito</div><div className="ma-row-v">{SCOPE_LABELS[b.territorial_scope]||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Procedura</div><div className="ma-row-v">{b.procedure||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Pubblicazione</div><div className="ma-row-v">{b.publication_date||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Apertura</div><div className="ma-row-v">{b.opening_date||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Chiusura</div><div className="ma-row-v">{b.closing_date_time?new Date(b.closing_date_time).toLocaleString("it-IT"):"—"}</div></div>
          </div>
          <div className="ma-info-block">
            <h3 className="ma-h3">Finanziamento</h3>
            <div className="ma-row-kv"><div className="ma-row-k">Tipologia aiuto</div><div className="ma-row-v">{b.aid_type||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Regime</div><div className="ma-row-v">{b.aid_regime||"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">% massima</div><div className="ma-row-v">{b.max_intensity_percent??"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Investim. min/max</div><div className="ma-row-v">{b.investment_min??"—"} / {b.investment_max??"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Agevol. min/max</div><div className="ma-row-v">{b.grant_min??"—"} / {b.grant_max??"—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Risorse disponibili</div><div className="ma-row-v">{b.available_resources??"—"}</div></div>
          </div>
          <div className="ma-info-block ma-col-full">
            <h3 className="ma-h3">Descrizione</h3>
            <div className="ma-notes">{b.description||"—"}</div>
          </div>
          {b.recipients && <div className="ma-info-block"><h3 className="ma-h3">Destinatari</h3><div className="ma-notes">{b.recipients}</div></div>}
          {b.requirements && <div className="ma-info-block"><h3 className="ma-h3">Requisiti</h3><div className="ma-notes">{b.requirements}</div></div>}
          {b.eligible_expenses && <div className="ma-info-block"><h3 className="ma-h3">Spese ammissibili</h3><div className="ma-notes">{b.eligible_expenses}</div></div>}
          {b.excluded_expenses && <div className="ma-info-block"><h3 className="ma-h3">Spese escluse</h3><div className="ma-notes">{b.excluded_expenses}</div></div>}
          {(b.official_url || b.legal_url) && <div className="ma-info-block ma-col-full">
            <h3 className="ma-h3">Link</h3>
            {b.official_url && <div><a className="ma-link" href={b.official_url} target="_blank" rel="noreferrer"><ExternalLink size={12}/> Pagina ufficiale</a></div>}
            {b.legal_url && <div><a className="ma-link" href={b.legal_url} target="_blank" rel="noreferrer"><ExternalLink size={12}/> Normativa</a></div>}
          </div>}
          {b.tags?.length > 0 && <div className="ma-info-block ma-col-full"><h3 className="ma-h3">Tag</h3>
            <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>{b.tags.map(t=><span key={t} className="ma-pill">{t}</span>)}</div>
          </div>}
        </div>
      )}

      {tab === "evals" && (
        <>
          <div style={{marginBottom:12, display:"flex", justifyContent:"flex-end"}}>
            <button className="ma-btn-primary-sm" onClick={()=>setAddEvalOpen(true)} data-testid="btn-add-eval"><Plus size={14}/> Nuova valutazione</button>
          </div>
          <div className="ma-table-wrap"><table className="ma-table">
            <thead><tr><th>Cliente</th><th>Stato valutazione</th><th>Motivazione</th><th>Note</th><th>Aggiornato</th></tr></thead>
            <tbody>
              {(b.evaluations||[]).map(ev => (
                <tr key={ev.id}>
                  <td><Link to={`/clients/${ev.subject_id}`} className="ma-link">{ev.subject_name}</Link></td>
                  <td><span className="ma-pill">{EVAL_LABELS[ev.status] || ev.status}</span></td>
                  <td className="ma-truncate">{ev.motivation||"—"}</td>
                  <td className="ma-truncate">{ev.notes||"—"}</td>
                  <td>{ev.updated_at ? new Date(ev.updated_at).toLocaleDateString("it-IT") : "—"}</td>
                </tr>
              ))}
              {(b.evaluations||[]).length===0 && <tr><td colSpan={5} className="ma-empty">Nessuna valutazione registrata.</td></tr>}
            </tbody>
          </table></div>
          {addEvalOpen && <AddEvalModal bandoId={id} onClose={()=>setAddEvalOpen(false)} onDone={()=>{setAddEvalOpen(false); load();}}/>}
        </>
      )}

      {tab === "practices" && (
        <div className="ma-table-wrap"><table className="ma-table">
          <thead><tr><th>Codice</th><th>Titolo</th><th>Cliente</th></tr></thead>
          <tbody>
            {(b.linked_practices||[]).map(p => (
              <tr key={p.id}><td><Link to={`/practices/${p.id}`} className="ma-link">{p.code}</Link></td>
                <td>{p.title}</td><td>{p.subject?.display_name||"—"}</td></tr>
            ))}
            {(b.linked_practices||[]).length===0 && <tr><td colSpan={3} className="ma-empty">Nessuna pratica collegata a questo bando.</td></tr>}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function AddEvalModal({ bandoId, onClose, onDone }) {
  const [subjects, setSubjects] = useState([]);
  const [f, setF] = useState({ subject_id:"", evaluation_status:"da_valutare", motivation:"", notes:"" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/subjects", { params: { limit: 200 } })
      .then(r => setSubjects(r.data.items || []))
      .catch(() => setSubjects([]));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); if (!f.subject_id) return;
    setBusy(true);
    try {
      await api.post("/bandi/evaluations", { ...f, bando_id: bandoId });
      toast.success("Valutazione salvata"); onDone();
    } catch { setBusy(false); }
  };
  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <div className="ma-modal-head">Associa cliente a questo bando</div>
        <form onSubmit={submit} className="ma-form-panel">
          <label className="ma-label">Cliente *</label>
          <select className="ma-input" required value={f.subject_id} onChange={e=>setF({...f, subject_id:e.target.value})} data-testid="eval-subject">
            <option value="">— Seleziona —</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
          </select>
          <label className="ma-label">Stato valutazione</label>
          <select className="ma-input" value={f.evaluation_status} onChange={e=>setF({...f, evaluation_status:e.target.value})}>
            {Object.entries(EVAL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="ma-label">Motivazione</label>
          <textarea className="ma-input" rows={2} value={f.motivation} onChange={e=>setF({...f, motivation:e.target.value})}/>
          <label className="ma-label">Note</label>
          <textarea className="ma-input" rows={2} value={f.notes} onChange={e=>setF({...f, notes:e.target.value})}/>
          <div className="ma-form-actions">
            <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="ma-btn-primary" disabled={busy}>{busy ? "Salvataggio…" : "Salva"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
